import { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Printer, MessageCircle, Undo2, Ban, Pencil, PackageCheck } from 'lucide-react';
import api, { apiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatIDR, formatDate, todayStr, formatSizes, PAY_STATUS, TX_STATUS, BARANG_STATUS, PAY_METHODS, sourceLabel, methodLabel } from '../lib/format';
import { MoneyInput } from '../components/MoneyInput';
import { TombolWa, KirimWaDialog } from '../components/KirimWa';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ReturnDialog } from '../components/ReturnDialog';
import { PickupDialog } from '../components/PickupDialog';
import { KartuDenda, KartuBarangSusulan } from '../components/OrderSusulan';
import CancelOrderDialog from '../components/CancelOrderDialog';

export default function TransactionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tx, setTx] = useState(null);
  const [ucapan, setUcapan] = useState(false);   // tawarkan ucapan terima kasih setelah barang diterima
  const [pickupOpen, setPickupOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [payDate, setPayDate] = useState(todayStr());   // tanggal uang diterima (bisa mundur)
  const [confirm, setConfirm] = useState(null);
  const [editPay, setEditPay] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', method: 'cash', date: '', kind: 'dp' });
  // Dulu dipaksa true, jadi karyawan melihat tombol yang pasti ditolak server.
  const { user } = useAuth();
  const isOwner = user?.role !== 'karyawan';

  const openEditPay = (p) => {
    setPayForm({ amount: p.amount, method: p.method, date: p.date, kind: p.kind || 'dp' });
    setEditPay(p);
  };

  const saveEditPay = async () => {
    try {
      await api.put(`/payments/${editPay.id}`, { ...payForm, amount: Number(payForm.amount) });
      toast.success('Pembayaran diperbarui');
      setEditPay(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const voidPay = async () => {
    try {
      await api.delete(`/payments/${editPay.id}`);
      toast.success('Pembayaran di-void (dihapus dari perhitungan)');
      setEditPay(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const load = useCallback(() => {
    api.get(`/transactions/${id}`).then((r) => setTx(r.data)).catch(() => toast.error('Order tidak ditemukan'));
  }, [id]);
  useEffect(() => {
    load();
    api.get('/settings').then((r) => setSettings(r.data));
  }, [load]);

  if (!tx) return <p className="text-sm text-mocha py-16 text-center">Memuat order…</p>;

  const addPayment = async () => {
    if (!Number(amount)) return toast.error('Isi jumlah pembayaran');
    if (!payDate) return toast.error('Isi tanggal pembayaran');
    if (payDate > todayStr()) return toast.error('Tanggal pembayaran tidak boleh di masa depan');
    try {
      await api.post(`/transactions/${id}/payments`, { amount: Number(amount), method, date: payDate });
      toast.success(`Pembayaran tercatat • ${formatDate(payDate)}`);
      setAmount('');
      setPayDate(todayStr());
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };


  const isActive = tx.status === 'active';
  const canManage = tx.status !== 'cancelled';
  const bs = BARANG_STATUS[tx.barang_status?.key];
  // Belum lengkap: sebagian barang masih di customer. Order tidak bisa diubah/dibatalkan
  // sampai pengembalian dilengkapi (server juga menolak).
  const terkunci = tx.barang_status?.key === 'belum_lengkap';
  const sisa = tx.sisa_kembali || [];
  const bisaDiambil = isActive && !tx.picked_up && tx.barang_status?.key === 'booked';

  return (
    <div className="space-y-5 max-w-3xl" data-testid="transaction-detail-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/transaksi" data-testid="back-to-transactions" className="p-2 rounded-xl bg-white border border-border text-brown">
          <ArrowLeft size={18} />
        </Link>
        <div>
          <h1 className="font-display text-2xl md:text-3xl text-ink tracking-tight">Order {tx.transaction_number}</h1>
          <div className="flex gap-1.5 mt-1 flex-wrap">
            <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${TX_STATUS[tx.status]?.cls}`}>{TX_STATUS[tx.status]?.label}</span>
            <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${PAY_STATUS[tx.payment_status]?.cls}`} data-testid="detail-payment-status">
              {PAY_STATUS[tx.payment_status]?.label}
            </span>
            {bs && <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${bs.cls}`}>{tx.barang_status.label}</span>}
          </div>
        </div>
      </div>

      {tx.status === 'cancelled' && (
        <div className="rounded-2xl border border-danger/30 bg-danger/5 p-4" data-testid="detail-cancel-info">
          <p className="text-sm font-bold text-danger">
            Order dibatalkan{tx.cancelled_date ? ` ${formatDate(tx.cancelled_date)}` : ''}
          </p>
          <p className="text-xs text-mocha mt-1">
            Alasan: {tx.cancel_reason || '—'}{tx.cancel_note ? ` (${tx.cancel_note})` : ''}
            {tx.cancelled_by ? ` • oleh ${tx.cancelled_by}` : ''}
          </p>
          {(tx.cancel_paid_total > 0 || tx.cancel_refunded > 0) && (
            <p className="text-xs text-mocha mt-1">
              Uang masuk {formatIDR(tx.cancel_paid_total || 0)} —
              {tx.cancel_forfeited > 0 && ` hangus ${formatIDR(tx.cancel_forfeited)} (jadi pemasukan)`}
              {tx.cancel_forfeited > 0 && tx.cancel_refunded > 0 && ','}
              {tx.cancel_refunded > 0 && ` dikembalikan ${formatIDR(tx.cancel_refunded)}`}
            </p>
          )}
        </div>
      )}

      {terkunci && sisa.length > 0 && (
        <div className="rounded-2xl border border-warning/40 bg-warning/10 p-4" data-testid="detail-belum-lengkap">
          <p className="text-sm font-bold text-[#8a5f1e]">
            Belum lengkap — {sisa.reduce((n, x) => n + (Number(x.qty) || 0), 0)} pcs masih di customer
          </p>
          <ul className="mt-1.5 space-y-0.5" data-testid="detail-sisa-list">
            {sisa.map((x, i) => (
              <li key={i} className="text-xs text-ink">
                {x.product_code} {x.product_name}{x.size ? ` (${x.size})` : ''} — <b>{x.qty} pcs</b>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-mocha mt-2">
            Stok barang ini tetap ditahan. Order selesai otomatis setelah semua sisa diterima.
          </p>
          {(tx.pengembalian_sebagian || []).length > 0 && (
            <div className="mt-2 border-t border-warning/30 pt-2" data-testid="detail-riwayat-kembali">
              <p className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Riwayat penerimaan</p>
              {tx.pengembalian_sebagian.map((t, i) => (
                <p key={i} className="text-[11px] text-mocha mt-0.5">
                  {formatDate(t.date)}: kembali {(t.kembali || []).reduce((n, x) => n + (x.pcs || 0), 0)} pcs
                  {(t.rusak || []).length > 0 && `, rusak ${t.rusak.reduce((n, x) => n + x.pcs, 0)}`}
                  {(t.hilang || []).length > 0 && `, hilang ${t.hilang.reduce((n, x) => n + x.pcs, 0)}`}
                  {(t.belum || []).length > 0 && `, belum ${t.belum.reduce((n, x) => n + x.qty, 0)}`}
                </p>
              ))}
            </div>
          )}
          <button
            data-testid="lengkapi-pengembalian-button"
            onClick={() => setConfirm('return')}
            className="mt-3 inline-flex items-center gap-2 rounded-xl bg-amber-600 text-white px-4 py-2 text-xs font-bold hover:bg-amber-700"
          >
            <Undo2 size={14} /> Lengkapi Pengembalian
          </button>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-border shadow-soft p-5 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Customer</p>
            <p className="font-semibold mt-1" data-testid="detail-customer-name">{tx.customer_name}</p>
            <p className="text-mocha text-xs">{tx.customer_whatsapp || '-'}</p>
            {tx.ktp_type === 'titip_ktp' && (
              <p className="text-mocha text-xs" data-testid="detail-jaminan">Jaminan: Titip KTP{tx.ktp_name ? ` — ${tx.ktp_name}` : ''}</p>
            )}
            {tx.ktp_type === 'kirim_wa' && (
              <p className="text-mocha text-xs" data-testid="detail-jaminan">Jaminan: Kirim WA</p>
            )}
            {!tx.ktp_type && tx.ktp && <p className="text-mocha text-xs">Jaminan: {tx.ktp}</p>}
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Jadwal</p>
            <p className="font-semibold mt-1">Pakai {formatDate(tx.use_date)}</p>
            <p className="text-xs text-mocha">
              Ambil {formatDate(tx.pickup_date)} → Kembali {formatDate(tx.return_date)}
              {tx.picked_up && tx.picked_up_date && (
                <span data-testid="detail-diambil" className="ml-1 text-success font-semibold">
                  • diambil {formatDate(tx.picked_up_date)}
                </span>
              )}
              {tx.custom_dates && <span className="ml-1 rounded bg-caramel/20 text-[#8a5a2e] px-1.5 py-0.5 text-[9px] font-bold">CUSTOM</span>}
            </p>
            <p className="text-mocha text-xs capitalize mt-0.5">
              {tx.event ? `${tx.event} • ` : ''}Sumber: {sourceLabel(tx.source)}
            </p>
          </div>
        </div>

        <div className="border-t border-dashed border-border pt-3 space-y-3">
          {tx.rows.map((r, idx) => (
            <div key={idx} className="flex items-start gap-3" data-testid={`detail-row-${r.product_code}`}>
              {r.photo && <img src={r.photo} alt={r.product_name} className="w-11 h-11 rounded-lg object-cover" loading="lazy" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold"><span className="text-brown">{r.product_code}</span> {r.product_name}</p>
                <p className="text-xs text-mocha">
                  {r.qty} pcs ({formatSizes(r.sizes)})
                  {r.bawahan_name && (
                    <> • bawahan {r.bawahan_custom ? 'custom' : 'standar'}: {r.bawahan_name}
                      {r.bawahan_sizes && Object.keys(r.bawahan_sizes).length > 0 && ` (${formatSizes(r.bawahan_sizes)})`}
                    </>
                  )}
                </p>
                {(r.aksesoris || []).length > 0 && (
                  <p className="text-xs text-mocha" data-testid={`detail-aksesoris-${r.product_code}`}>
                    Aksesoris: {r.aksesoris.map((a) => `${a.nama} ×${a.jumlah}`).join(', ')}
                  </p>
                )}
              </div>
              <p className="text-sm font-semibold whitespace-nowrap">{formatIDR(r.subtotal)}</p>
            </div>
          ))}
          {(tx.pengembalian?.aksesoris_hilang || []).length > 0 && (
            <div className="rounded-xl bg-orange-50 text-orange-800 text-xs px-3 py-2" data-testid="detail-aksesoris-hilang">
              <b>Aksesoris hilang:</b>{' '}
              {tx.pengembalian.aksesoris_hilang.map((a) => `${a.nama} ${a.pcs} pcs`).join(', ')}
              {!(tx.denda && ['belum', 'lunas'].includes(tx.denda.status)) && (
                <> — denda belum ditetapkan{isOwner ? '. Isi lewat kartu Denda di bawah.' : '. Owner yang mengisinya.'}</>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-dashed border-border pt-3 space-y-1 text-sm">
          <div className="flex justify-between font-semibold"><span>Total ({tx.pcs} pcs)</span><span data-testid="detail-total">{formatIDR(tx.total)}</span></div>
          <div className="flex justify-between text-mocha"><span>Dibayar ({methodLabel(tx.payment_method)})</span><span data-testid="detail-paid">{formatIDR(tx.paid)}</span></div>
          <div className="flex justify-between font-bold text-brown"><span>Sisa</span><span data-testid="detail-remaining">{formatIDR(tx.remaining)}</span></div>
        </div>

        {tx.notes && <p className="text-xs text-mocha bg-ivory rounded-xl px-4 py-3">Catatan: {tx.notes}</p>}
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-soft p-5">
        <h2 className="font-display text-lg text-brown-dark mb-3">Riwayat Pembayaran</h2>
        {tx.payments?.length ? (
          <div className="space-y-2 mb-4">
            {tx.payments.map((p) => (
              <div key={p.id} data-testid={`payment-row-${p.id.slice(0, 8)}`} className={`flex justify-between items-center text-sm bg-ivory rounded-xl px-4 py-2.5 ${p.voided ? 'opacity-40' : ''}`}>
                <span className={p.voided ? 'line-through' : ''}>
                  <b className="text-brown">{p.kind === 'pelunasan' ? 'Pelunasan' : ['dp', 'deposit'].includes(p.kind) ? 'DP' : 'Pembayaran'}</b>
                  {' '}• {methodLabel(p.method)} • {formatDate(p.date)}
                  {p.voided && <span className="text-danger text-[10px] font-bold ml-1.5 no-underline">VOID</span>}
                </span>
                <span className="flex items-center gap-1">
                  <span className="font-semibold">{formatIDR(p.amount)}</span>
                  {!p.voided && (
                    <button data-testid={`edit-payment-${p.id.slice(0, 8)}`} onClick={() => openEditPay(p)} className="p-1.5 rounded-lg text-brown hover:bg-brown/10" title="Edit pembayaran">
                      <Pencil size={13} />
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-mocha mb-4">Belum ada pembayaran.</p>
        )}
        {canManage && tx.remaining > 0 && (
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[140px]">
              <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Tambah Pembayaran</label>
              <MoneyInput
                data-testid="payment-amount-input"
                value={amount}
                onChange={setAmount}
                placeholder={`Sisa ${formatIDR(tx.remaining)}`}
                className="mt-1 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30"
              />
            </div>
            <div className="w-[150px]">
              <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Tanggal</label>
              <input
                data-testid="payment-date-input"
                type="date"
                value={payDate}
                max={todayStr()}
                onChange={(e) => setPayDate(e.target.value)}
                className="mt-1 w-full rounded-xl border border-input bg-ivory/50 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30"
              />
            </div>
            <div className="flex gap-1.5">
              {PAY_METHODS.slice(0, 3).map((m) => (
                <button
                  key={m.value}
                  data-testid={`pay-method-${m.value}`}
                  onClick={() => setMethod(m.value)}
                  className={`rounded-full px-3 py-2 text-xs font-semibold ${method === m.value ? 'bg-brown text-white' : 'bg-ivory text-mocha'}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <button
              data-testid="add-payment-button"
              onClick={addPayment}
              className="rounded-xl bg-brown text-white px-5 py-2.5 text-sm font-semibold hover:bg-brown-med transition-colors"
            >
              Catat
            </button>
          </div>
        )}
      </div>

      {/* Jalur perbaikan kalau saat Terima Pengembalian ada yang terlupa. */}
      {isOwner && tx.status === 'completed' && (
        <>
          <KartuDenda tx={tx} onDone={load} />
          <KartuBarangSusulan tx={tx} onDone={load} />
        </>
      )}

      <div className="flex flex-wrap gap-2">
        <Link
          to={`/nota/${tx.id}`}
          data-testid="view-receipt-button"
          className="inline-flex items-center gap-2 rounded-xl bg-gold text-white px-5 py-2.5 text-sm font-semibold hover:bg-gold-dark transition-colors"
        >
          <Printer size={15} /> Lihat & Cetak Nota
        </Link>
        <TombolWa
          target={{ kind: 'order', id: tx.id }}
          testid="send-whatsapp-button"
          title="Kirim pesan WhatsApp ke customer"
          className="inline-flex items-center gap-2 rounded-xl bg-[#25D366] text-white px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          <MessageCircle size={15} /> Kirim via WhatsApp
        </TombolWa>
        {canManage && (
          <>
            {bisaDiambil && (
              <button
                data-testid="pickup-detail-button"
                onClick={() => setPickupOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-success text-white px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                <PackageCheck size={15} /> Tandai Diambil
              </button>
            )}
            {!terkunci && (
              <button
                data-testid="edit-transaction-button"
                onClick={() => navigate(`/transaksi/${tx.id}/edit`)}
                className="inline-flex items-center gap-2 rounded-xl bg-caramel text-white px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                <Pencil size={15} /> Edit Order
              </button>
            )}
            {isActive && !terkunci && (
              <button
                data-testid="return-items-button"
                onClick={() => setConfirm('return')}
                className="inline-flex items-center gap-2 rounded-xl bg-brown text-white px-5 py-2.5 text-sm font-semibold hover:bg-brown-med transition-colors"
              >
                <Undo2 size={15} /> Terima Pengembalian
              </button>
            )}
            {!terkunci && (
              <button
                data-testid="cancel-transaction-button"
                onClick={() => setConfirm('cancel')}
                className="inline-flex items-center gap-2 rounded-xl border border-danger/30 text-danger px-5 py-2.5 text-sm font-semibold hover:bg-danger/5 transition-colors"
              >
                <Ban size={15} /> Batalkan
              </button>
            )}
          </>
        )}
      </div>

      <Dialog open={!!editPay} onOpenChange={() => setEditPay(null)}>
        <DialogContent data-testid="edit-payment-dialog" className="bg-ivory max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-brown-dark">Edit Pembayaran</DialogTitle></DialogHeader>
          <div className="space-y-2.5">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Nominal (Rp)</label>
              <MoneyInput data-testid="edit-payment-amount" value={payForm.amount}
                onChange={(v) => setPayForm({ ...payForm, amount: v })}
                className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Metode</label>
                <select data-testid="edit-payment-method" value={payForm.method}
                  onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none">
                  {PAY_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Jenis</label>
                <select data-testid="edit-payment-kind" value={payForm.kind}
                  onChange={(e) => setPayForm({ ...payForm, kind: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none">
                  <option value="dp">DP</option>
                  <option value="pelunasan">Pelunasan</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Tanggal Pembayaran</label>
              <input data-testid="edit-payment-date" type="date" value={payForm.date}
                onChange={(e) => setPayForm({ ...payForm, date: e.target.value })}
                className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none" />
            </div>
            <button data-testid="edit-payment-save" onClick={saveEditPay}
              className="w-full rounded-xl bg-brown text-white py-3 text-sm font-bold hover:bg-brown-med">Simpan Perubahan</button>
            <button data-testid="edit-payment-void" onClick={voidPay}
              className="w-full rounded-xl border border-danger/30 text-danger py-2.5 text-xs font-bold hover:bg-danger/5">Void Pembayaran (salah catat)</button>
          </div>
        </DialogContent>
      </Dialog>

      {ucapan && (
        <KirimWaDialog open onClose={() => setUcapan(false)} target={{ kind: 'order', id: tx.id, jenis: 'terima_kasih' }} />
      )}

      <PickupDialog tx={tx} open={pickupOpen} onClose={() => setPickupOpen(false)} onDone={() => load()} />

      <ReturnDialog
        tx={tx}
        open={confirm === 'return'}
        onClose={() => setConfirm(null)}
        onDone={() => { load(); setUcapan(true); }}
        testid="return"
      />

      <CancelOrderDialog
        tx={tx}
        open={confirm === 'cancel'}
        onClose={() => setConfirm(null)}
        onDone={load}
      />
    </div>
  );
}
